<template>
  <Teleport to="body">
    <div 
      class="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-black/60 backdrop-blur-sm"
      @click.self="$emit('close')"
    >
      <div 
        class="bg-white/95 dark:bg-slate-800/95 backdrop-blur-sm rounded-3xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-hidden border border-white/20 dark:border-slate-700/50"
        @click.stop
      >
        <!-- Header -->
        <div class="bg-gradient-to-r from-slate-100 to-slate-50 dark:from-slate-700 dark:to-slate-800 px-4 sm:px-5 py-4 flex items-center justify-between border-b border-slate-200/50 dark:border-slate-600/50 rounded-t-3xl">
          <h2 class="font-semibold text-slate-800 dark:text-white truncate">Kontaktdetails</h2>
          <button
            @click="$emit('close')"
            class="p-1.5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-200/50 dark:hover:bg-slate-600/50 rounded-xl transition-colors"
          >
            <X class="w-5 h-5" />
          </button>
        </div>

        <!-- Profile -->
        <div class="p-4 sm:p-5 border-b border-slate-100/50 dark:border-slate-700/50">
          <div class="flex items-center gap-4">
            <div class="relative">
              <img
                v-if="photoUrl"
                :src="photoUrl"
                :alt="contact.display_name"
                class="w-16 h-16 rounded-2xl object-cover ring-2 ring-white/50 dark:ring-slate-700/50 shadow-lg"
              >
              <div
                v-else
                class="w-16 h-16 rounded-2xl bg-gradient-to-br from-slate-400 to-slate-600 flex items-center justify-center shadow-lg"
              >
                <span class="text-xl font-medium text-white">{{ initials }}</span>
              </div>
              <div
                v-if="contact.entra_id"
                class="absolute -bottom-1 -right-1 w-4 h-4 rounded-full border-2 border-white dark:border-slate-800 shadow-sm"
                :class="presenceClass"
              ></div>
            </div>
            <div class="flex-1 min-w-0">
              <h3 class="font-semibold text-slate-900 dark:text-white truncate">{{ contact.display_name }}</h3>
              <p v-if="contact.job_title" class="text-sm text-slate-600 dark:text-slate-400">{{ contact.job_title }}</p>
              <p v-if="contact.company_name" class="text-sm text-slate-400">{{ contact.company_name }}</p>
            </div>
          </div>

          <!-- Tags -->
          <div class="flex flex-wrap gap-2 mt-4 text-xs text-slate-500 dark:text-slate-400">
            <span v-if="contact.department" class="px-2 py-0.5 bg-slate-100 dark:bg-slate-700 rounded-full">{{ contact.department }}</span>
            <span v-if="contact.is_entra_user" class="px-2 py-0.5 bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400 rounded-full">Entra ID</span>
          </div>
        </div>

        <!-- Actions -->
        <div class="p-3 sm:p-4 grid grid-cols-4 gap-2 border-b border-slate-100/50 dark:border-slate-700/50">
          <button
            v-if="hasValidEmail"
            @click="openTeamsChat"
            class="flex flex-col items-center gap-1.5 p-3 bg-blue-100/80 dark:bg-blue-900/40 hover:bg-blue-200 dark:hover:bg-blue-900/60 text-blue-600 dark:text-blue-400 text-xs font-medium rounded-xl transition-colors"
          >
            <MessageSquare class="w-4 h-4" />
            Nachricht
          </button>
          <button
            v-if="hasPhone"
            @click="makePhoneCall"
            class="flex flex-col items-center gap-1.5 p-3 bg-green-100/80 dark:bg-green-900/40 hover:bg-green-200 dark:hover:bg-green-900/60 text-green-600 dark:text-green-400 text-xs font-medium rounded-xl transition-colors relative"
          >
            <Phone class="w-4 h-4" />
            Anrufen
            <Heart v-if="currentCallPref" class="absolute top-1 right-1 w-2.5 h-2.5 text-red-500 fill-red-500" />
          </button>
          <button
            v-if="hasValidEmail"
            @click="openTeamsVideoCall"
            class="flex flex-col items-center gap-1.5 p-3 bg-purple-100/80 dark:bg-purple-900/40 hover:bg-purple-200 dark:hover:bg-purple-900/60 text-purple-600 dark:text-purple-400 text-xs font-medium rounded-xl transition-colors"
          >
            <Video class="w-4 h-4" />
            Video
          </button>
          <a
            v-if="hasValidEmail"
            :href="`mailto:${contact.email}`"
            class="flex flex-col items-center gap-1.5 p-3 bg-orange-100/80 dark:bg-orange-900/40 hover:bg-orange-200 dark:hover:bg-orange-900/60 text-orange-600 dark:text-orange-400 text-xs font-medium rounded-xl transition-colors"
          >
            <Mail class="w-4 h-4" />
            E-Mail
          </a>
        </div>

        <!-- Favorite Call Method -->
        <div v-if="hasPhone || hasValidEmail" class="px-4 pt-3 pb-1">
          <p class="text-[10px] font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-2 flex items-center gap-1.5">
            <Heart class="w-3 h-3" />
            Standard-Anrufmethode
          </p>
          <div class="flex flex-wrap gap-1.5">
            <button
              v-if="hasValidEmail"
              @click.stop="toggleCallFavorite('teams')"
              :class="[
                'flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-all',
                currentCallPref === 'teams'
                  ? 'bg-purple-100 dark:bg-purple-900/50 text-purple-700 dark:text-purple-300 ring-1 ring-purple-300 dark:ring-purple-600'
                  : 'bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-600'
              ]"
            >
              <Heart class="w-3 h-3" :class="currentCallPref === 'teams' ? 'fill-purple-500 text-purple-500' : ''" />
              Teams
            </button>
            <button
              v-if="businessPhones.length > 0"
              @click.stop="toggleCallFavorite('business')"
              :class="[
                'flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-all',
                currentCallPref === 'business'
                  ? 'bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300 ring-1 ring-green-300 dark:ring-green-600'
                  : 'bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-600'
              ]"
            >
              <Heart class="w-3 h-3" :class="currentCallPref === 'business' ? 'fill-green-500 text-green-500' : ''" />
              Telefon
            </button>
            <button
              v-if="contact.mobile_phone"
              @click.stop="toggleCallFavorite('mobile')"
              :class="[
                'flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-all',
                currentCallPref === 'mobile'
                  ? 'bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 ring-1 ring-blue-300 dark:ring-blue-600'
                  : 'bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-600'
              ]"
            >
              <Heart class="w-3 h-3" :class="currentCallPref === 'mobile' ? 'fill-blue-500 text-blue-500' : ''" />
              Mobil
            </button>
          </div>
        </div>

        <!-- Details -->
        <div class="p-4 space-y-1 overflow-y-auto max-h-72">
          <!-- Email -->
          <div v-if="hasValidEmail" class="flex items-center gap-3 p-2.5 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700/50 cursor-pointer transition-colors" @click="copyToClipboard(contact.email)">
            <div class="w-8 h-8 rounded-lg bg-slate-100 dark:bg-slate-700 flex items-center justify-center">
              <Mail class="w-4 h-4 text-slate-600 dark:text-slate-400" />
            </div>
            <div class="flex-1 min-w-0">
              <p class="text-xs text-slate-400 dark:text-slate-500">E-Mail</p>
              <p class="text-sm text-slate-700 dark:text-slate-200 truncate">{{ contact.email }}</p>
            </div>
          </div>
          
          <!-- Mobile Phone -->
          <div v-if="contact.mobile_phone" class="flex items-center gap-3 p-2.5 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700/50 cursor-pointer transition-colors" @click="copyToClipboard(contact.mobile_phone)">
            <div class="w-8 h-8 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
              <Smartphone class="w-4 h-4 text-blue-600 dark:text-blue-400" />
            </div>
            <div class="flex-1 min-w-0">
              <p class="text-xs text-slate-400 dark:text-slate-500">Mobil</p>
              <p class="text-sm text-slate-700 dark:text-slate-200">{{ contact.mobile_phone }}</p>
            </div>
          </div>
          
          <!-- Business Phones -->
          <div v-for="(phone, idx) in businessPhones" :key="phone" class="flex items-center gap-3 p-2.5 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700/50 cursor-pointer transition-colors" @click="copyToClipboard(phone)">
            <div class="w-8 h-8 rounded-lg bg-slate-100 dark:bg-slate-700 flex items-center justify-center">
              <Phone class="w-4 h-4 text-slate-600 dark:text-slate-400" />
            </div>
            <div class="flex-1 min-w-0">
              <p class="text-xs text-slate-400 dark:text-slate-500">Telefon {{ businessPhones.length > 1 ? idx + 1 : '' }}</p>
              <p class="text-sm text-slate-700 dark:text-slate-200">{{ phone }}</p>
            </div>
          </div>
          
          <!-- Department -->
          <div v-if="contact.department" class="flex items-center gap-3 p-2.5 rounded-lg">
            <div class="w-8 h-8 rounded-lg bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
              <Building class="w-4 h-4 text-purple-600 dark:text-purple-400" />
            </div>
            <div class="flex-1 min-w-0">
              <p class="text-xs text-slate-400 dark:text-slate-500">Abteilung</p>
              <p class="text-sm text-slate-700 dark:text-slate-200">{{ contact.department }}</p>
            </div>
          </div>
          
          <!-- Office Location -->
          <div v-if="contact.office_location" class="flex items-center gap-3 p-2.5 rounded-lg">
            <div class="w-8 h-8 rounded-lg bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
              <MapPin class="w-4 h-4 text-amber-600 dark:text-amber-400" />
            </div>
            <div class="flex-1 min-w-0">
              <p class="text-xs text-slate-400 dark:text-slate-500">Büro</p>
              <p class="text-sm text-slate-700 dark:text-slate-200">{{ contact.office_location }}</p>
            </div>
          </div>
          
          <!-- Address (City, Street, Country) -->
          <div v-if="fullAddress" class="flex items-center gap-3 p-2.5 rounded-lg">
            <div class="w-8 h-8 rounded-lg bg-rose-100 dark:bg-rose-900/30 flex items-center justify-center">
              <Home class="w-4 h-4 text-rose-600 dark:text-rose-400" />
            </div>
            <div class="flex-1 min-w-0">
              <p class="text-xs text-slate-400 dark:text-slate-500">Adresse</p>
              <p class="text-sm text-slate-700 dark:text-slate-200">{{ fullAddress }}</p>
            </div>
          </div>
        </div>

        <!-- Add to Contacts (for ALL users now, not just non-Entra) -->
        <div v-if="hasValidEmail" class="p-4 border-t border-slate-100 dark:border-slate-700">
          <div class="flex gap-2">
            <button
              @click="addToMyContacts"
              :disabled="isAddingContact || addedToContacts"
              class="flex-1 py-2.5 text-sm font-medium rounded-lg flex items-center justify-center gap-2 transition-colors"
              :class="addedToContacts 
                ? 'bg-green-50 dark:bg-green-900/30 text-green-600 dark:text-green-400' 
                : 'bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200'"
            >
              <UserPlus v-if="!addedToContacts && !isAddingContact" class="w-4 h-4" />
              <Check v-if="addedToContacts" class="w-4 h-4" />
              <Loader2 v-if="isAddingContact" class="w-4 h-4 animate-spin" />
              {{ addedToContacts ? 'Hinzugefügt' : 'Zu Outlook hinzufügen' }}
            </button>
            <button
              @click="showQrCode = true"
              class="py-2.5 px-3 text-sm font-medium rounded-lg flex items-center justify-center gap-2 transition-colors bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200"
              title="QR-Code vCard"
            >
              <QrCode class="w-4 h-4" />
            </button>
          </div>
        </div>

        <!-- Toast -->
        <div v-if="toastMessage" class="absolute bottom-4 left-1/2 -translate-x-1/2 px-4 py-2 bg-slate-800 text-white text-sm rounded-full shadow-lg">
          {{ toastMessage }}
        </div>
      </div>
    </div>
  </Teleport>

  <!-- QR Code Modal - Digitale Visitenkarte -->
  <Teleport to="body">
    <div
      v-if="showQrCode"
      class="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      @click.self="showQrCode = false"
    >
      <div class="relative bg-white dark:bg-slate-800 rounded-2xl shadow-2xl max-w-sm w-full overflow-hidden border border-white/20 dark:border-slate-700/50" @click.stop>
        <button @click="showQrCode = false" class="absolute z-10 top-3 right-3 p-1.5 text-white/70 hover:text-white bg-slate-900/25 hover:bg-slate-900/40 rounded-lg transition-colors">
          <X class="w-4 h-4" />
        </button>
        <div class="p-3 sm:p-4 flex justify-center">
          <BrandedBusinessCard :contact="contact" :photo-url="photoUrl">
            <template #qr><canvas ref="qrCanvas" /></template>
          </BrandedBusinessCard>
        </div>
        <p class="text-[11px] text-slate-400 dark:text-slate-500 text-center px-4 mb-2">Scannen, um den Kontakt zu speichern</p>
        <!-- Actions -->
        <div class="px-4 pb-4 flex gap-2">
          <button
            @click="downloadQrCode"
            class="flex-1 py-2 text-sm font-medium rounded-lg bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200 flex items-center justify-center gap-2 transition-colors"
          >
            <Download class="w-4 h-4" />
            QR-Code
          </button>
          <button
            @click="downloadVcf"
            class="flex-1 py-2 text-sm font-medium rounded-lg bg-blue-100 dark:bg-blue-900/40 hover:bg-blue-200 dark:hover:bg-blue-900/60 text-blue-600 dark:text-blue-400 flex items-center justify-center gap-2 transition-colors"
          >
            <Download class="w-4 h-4" />
            .vcf mit Foto
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { ref, computed, onMounted, watch, nextTick } from 'vue';
import { X, MessageSquare, Phone, Video, Mail, Smartphone, MapPin, Building, Home, UserPlus, Check, Loader2, QrCode, Download, Heart } from 'lucide-vue-next';
import { fetchContactPhoto, getPresenceClass, addContactToOutlook } from '../websocketService';
import { getCallPreference, setCallPreference, removeCallPreference } from '../localStorageService';
import { makePhoneCall as callServiceMakePhoneCall } from '../callService';
import QRCode from 'qrcode';
import BrandedBusinessCard from './BrandedBusinessCard.vue';
import { buildVCard } from '../utils/vcard.mjs';

const props = defineProps({
  contact: { type: Object, required: true }
});

defineEmits(['close']);

const photoUrl = ref(null);
const photoBase64 = ref(null);
const isAddingContact = ref(false);
const addedToContacts = ref(false);
const toastMessage = ref('');
const showQrCode = ref(false);
const qrCanvas = ref(null);

const initials = computed(() => {
  const name = props.contact.display_name || '';
  return name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase() || '?';
});

const presenceClass = computed(() => getPresenceClass(props.contact.entra_id));

const hasValidEmail = computed(() => {
  const email = props.contact.email;
  return email && !email.endsWith('@mailbox-contact.local');
});

const hasPhone = computed(() => props.contact.mobile_phone || businessPhones.value.length > 0);

const primaryPhone = computed(() => props.contact.mobile_phone || businessPhones.value[0] || null);

const businessPhones = computed(() => {
  const phones = props.contact.business_phones;
  if (typeof phones === 'string') {
    try { return JSON.parse(phones); } catch { return []; }
  }
  return Array.isArray(phones) ? phones : [];
});

// Call preference (favorite call method)
const currentCallPref = computed(() => getCallPreference(props.contact.email));

const toggleCallFavorite = (method) => {
  if (currentCallPref.value === method) {
    removeCallPreference(props.contact.email);
  } else {
    setCallPreference(props.contact.email, method);
  }
};

const fullAddress = computed(() => {
  const parts = [];
  if (props.contact.street_address) parts.push(props.contact.street_address);
  if (props.contact.postal_code || props.contact.city) {
    parts.push([props.contact.postal_code, props.contact.city].filter(Boolean).join(' '));
  }
  if (props.contact.country) parts.push(props.contact.country);
  return parts.length > 0 ? parts.join(', ') : null;
});

// Convert blob URL to base64 for vCard embedding
const fetchPhotoAsBase64 = async () => {
  if (!photoUrl.value) return null;
  try {
    const response = await fetch(photoUrl.value);
    const blob = await response.blob();
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result.split(',')[1]);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch { return null; }
};

onMounted(async () => {
  if (props.contact.is_entra_user && props.contact.entra_id && props.contact.email) {
    photoUrl.value = await fetchContactPhoto(props.contact.email, props.contact.entra_id);
    if (photoUrl.value) {
      photoBase64.value = await fetchPhotoAsBase64();
    }
  }
});

const openTeamsChat = () => {
  window.open(`https://teams.microsoft.com/l/chat/0/0?users=${encodeURIComponent(props.contact.email)}`, '_blank');
};

const makePhoneCall = () => {
  if (primaryPhone.value) callServiceMakePhoneCall(primaryPhone.value);
};

const openTeamsVideoCall = () => {
  window.open(`https://teams.microsoft.com/l/call/0/0?users=${encodeURIComponent(props.contact.email)}&withVideo=true`, '_blank');
};

const copyToClipboard = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    toastMessage.value = 'Kopiert!';
    setTimeout(() => toastMessage.value = '', 1500);
  } catch (e) { console.error(e); }
};

const addToMyContacts = async () => {
  if (isAddingContact.value || addedToContacts.value) return;
  isAddingContact.value = true;
  try {
    await addContactToOutlook(props.contact);
    addedToContacts.value = true;
    toastMessage.value = 'Hinzugefügt!';
    setTimeout(() => toastMessage.value = '', 1500);
  } catch (e) {
    toastMessage.value = 'Fehler';
    setTimeout(() => toastMessage.value = '', 1500);
  } finally {
    isAddingContact.value = false;
  }
};

const renderQrCode = async () => {
  await nextTick();
  if (!qrCanvas.value) return;
  const vcard = buildVCard(props.contact); // without photo (too large for QR)
  try {
    await QRCode.toCanvas(qrCanvas.value, vcard, {
      width: 280,
      margin: 1,
      errorCorrectionLevel: 'H',
      color: { dark: '#1e293b', light: '#ffffff' }
    });

    // Overlay employee photo in center of QR code
    if (photoUrl.value) {
      const canvas = qrCanvas.value;
      const ctx = canvas.getContext('2d');
      const size = canvas.width;

      const img = new Image();
      img.crossOrigin = 'anonymous';
      await new Promise((resolve) => {
        img.onload = resolve;
        img.onerror = resolve;
        img.src = photoUrl.value;
      });

      if (img.complete && img.naturalWidth > 0) {
        const photoSize = Math.round(size * 0.22);
        const x = (size - photoSize) / 2;
        const y = (size - photoSize) / 2;
        const border = Math.round(size * 0.018);

        // White circular border
        ctx.beginPath();
        ctx.arc(size / 2, size / 2, photoSize / 2 + border, 0, Math.PI * 2);
        ctx.fillStyle = '#ffffff';
        ctx.fill();

        // Circular photo
        ctx.save();
        ctx.beginPath();
        ctx.arc(size / 2, size / 2, photoSize / 2, 0, Math.PI * 2);
        ctx.clip();
        ctx.drawImage(img, x, y, photoSize, photoSize);
        ctx.restore();
      }
    }
  } catch (e) {
    console.error('QR generation failed:', e);
  }
};

watch(showQrCode, (val) => {
  if (val) renderQrCode();
});

const downloadQrCode = () => {
  if (!qrCanvas.value) return;
  const link = document.createElement('a');
  link.download = `${props.contact.display_name.replace(/\s+/g, '_')}_QR.png`;
  link.href = qrCanvas.value.toDataURL('image/png');
  link.click();
};

const downloadVcf = () => {
  const vcard = buildVCard(props.contact, { includePhoto: true, photoBase64: photoBase64.value });
  const blob = new Blob([vcard], { type: 'text/vcard;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${props.contact.display_name.replace(/\s+/g, '_')}.vcf`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};
</script>
